import { type FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
// Import UI Components
import { Button } from '@geneui/components';
// Import Components
import FilterFields from 'components/common/FilterFields';
// Import Hooks
import useFilterFieldsState from 'hooks/UseFilterFieldsState';
import useApplyOnEnter from 'hooks/UseApplyOnEnter';
// Import Services
import { FilterExistingValueChecker, FiltersFieldsWithComparisionMap } from 'components/common/FilterFields/service';
// Import Constants
import { l, SupportedComparisonFilterFieldsTypes } from 'constants/common';
// Import Styles
import 'assets/scss/clientList.scss';

interface Props {
    filtersList: AnyObject[];
    isApplyOnEnterEnabled?: boolean;
    onApply: (data: AnyObject) => void;
    onReset: () => void;
}

const SegmentationFilters: FC<Props> = ({ filtersList, isApplyOnEnterEnabled = true, onApply, onReset }) => {
    const {
        filters,
        appliedFilters,
        changeFilters,
        applyFilters: applyChangedFilters,
        resetFilters,
        isResetDisabled,
    } = useFilterFieldsState(filtersList);

    const { t } = useTranslation();

    const applyFilters = (data = filters, withOnApply) => {
        const resultData = FiltersFieldsWithComparisionMap(data);
        withOnApply && onApply(resultData);
    };

    const handleChange = (e, changeInputValue, customChange, index, filterInputData) => {
        const resultData = changeInputValue(e, customChange, cloneDeep(filters), index, filterInputData);
        changeFilters(resultData);
    };

    const handleComparisonChange = (comparison, index) => {
        const data = cloneDeep(filters);
        data[index].comparision = comparison.Comparision;
        data[index].type = SupportedComparisonFilterFieldsTypes[comparison.Type];
        changeFilters(data);
    };

    const handleReset = () => {
        resetFilters();
        onReset();
    };

    const handleApply = () => {
        if (FilterExistingValueChecker(filters)) {
            applyChangedFilters();
            applyFilters(filters, true);
        }
    };

    const isApplyDisabled = useMemo(() => {
        const currentFilters = filters.reduce((acc, val) => {
            acc[val.ColumnId] = val;
            return acc;
        }, {});

        return !appliedFilters.reduce((acc, val) => {
            const currentFilter = currentFilters[val.ColumnId];
            if (isEqual(val.value, currentFilter.value)) {
                if (!isEmpty(val.value) && !isEqual(val.comparision, currentFilter.comparision)) {
                    return true;
                }
                return acc;
            }

            return true;
        }, false);
    }, [filters, appliedFilters]);

    // `isApplyDisabled` is used instead of `hasUnappliedChanges`: here it is not debounced and it ignores
    // a comparison change on an empty filter, so Enter follows the Apply button state
    const { containerRef, handleInteraction } = useApplyOnEnter<HTMLDivElement>({
        onApply: handleApply,
        canApply: () => !isApplyDisabled,
        isEnabled: isApplyOnEnterEnabled,
    });

    return (
        <div ref={containerRef} onFocus={handleInteraction} onMouseDown={handleInteraction}>
            <div className="crm-horizontal-filters-container">
                <FilterFields
                    filterInputsData={filters}
                    handleChange={handleChange}
                    withComparisons={true}
                    handleComparisonChange={handleComparisonChange}
                />
            </div>
            <div className="crm-filters-action-buttons">
                <Button appearance="minimal" onClick={handleReset} disabled={isResetDisabled}>
                    {t(l.Reset)}
                </Button>
                <Button color="confirm" onClick={handleApply} disabled={isApplyDisabled}>
                    {t(l.Apply)}
                </Button>
            </div>
        </div>
    );
};

export default memo(SegmentationFilters);
